/**
 * BBoxCanvas — interactive bounding box annotation canvas.
 *
 * Supports: draw new boxes, select/move/resize existing boxes, delete.
 * All coordinates are normalized (0-1). The canvas handles pixel conversion.
 */

import { useRef, useState, useEffect, useCallback } from 'react'

export interface BBox {
  id: string // client-side UUID for tracking
  class_id: number
  class_name: string
  x_center: number  // normalized 0-1
  y_center: number
  width: number
  height: number
  confidence: number | null
  source: 'auto' | 'manual' | 'corrected'
  color: string
}

interface BBoxCanvasProps {
  imageUrl: string
  boxes: BBox[]
  activeClassId: number
  activeClassName: string
  activeColor: string
  onChange: (boxes: BBox[]) => void
  onSelect: (box: BBox | null) => void
  selectedBoxId: string | null
}

type Mode = 'idle' | 'drawing' | 'moving' | 'resizing'
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se'

const HANDLE_SIZE = 8
const MIN_BOX_SIZE = 0.01 // minimum normalized dimension

function generateId(): string {
  return Math.random().toString(36).slice(2, 10)
}

export function BBoxCanvas({
  imageUrl,
  boxes,
  activeClassId,
  activeClassName,
  activeColor,
  onChange,
  onSelect,
  selectedBoxId,
}: BBoxCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 })

  // Interaction state
  const [mode, setMode] = useState<Mode>('idle')
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null)
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null)
  const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number } | null>(null)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle | null>(null)
  const [resizeOriginal, setResizeOriginal] = useState<BBox | null>(null)

  // Load image
  useEffect(() => {
    if (!imageUrl) return
    const img = new Image()
    // Note: not setting crossOrigin — S3 bucket has no CORS config.
    // Canvas drawImage works fine without it. We don't need pixel export.
    img.onload = () => {
      imageRef.current = img
      setImageLoaded(true)
    }
    img.onerror = () => setImageLoaded(false)
    img.src = imageUrl
  }, [imageUrl])

  // Resize canvas to fit container while maintaining aspect ratio
  useEffect(() => {
    if (!imageLoaded || !imageRef.current || !containerRef.current) return

    const fitCanvas = () => {
      const container = containerRef.current
      const img = imageRef.current
      if (!container || !img) return

      const aspect = img.naturalWidth / img.naturalHeight
      const maxW = container.clientWidth || 800
      const maxH = window.innerHeight * 0.7

      let w = maxW
      let h = w / aspect
      if (h > maxH) {
        h = maxH
        w = h * aspect
      }

      const newW = Math.floor(w)
      const newH = Math.floor(h)
      if (newW > 0 && newH > 0) {
        setCanvasSize({ w: newW, h: newH })
      }
    }

    // Wait for layout to complete
    requestAnimationFrame(fitCanvas)
  }, [imageLoaded])

  // Convert normalized coords to pixel coords
  const toPixel = useCallback((nx: number, ny: number) => ({
    x: nx * canvasSize.w,
    y: ny * canvasSize.h,
  }), [canvasSize])

  // Convert pixel coords to normalized
  const toNorm = useCallback((px: number, py: number) => ({
    x: Math.max(0, Math.min(1, px / canvasSize.w)),
    y: Math.max(0, Math.min(1, py / canvasSize.h)),
  }), [canvasSize])

  // Get mouse position relative to canvas
  const getMousePos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }, [])

  // Draw everything
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    const img = imageRef.current
    if (!canvas || !ctx || !img || canvasSize.w === 0) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    // Draw existing boxes
    for (const box of boxes) {
      const x1 = (box.x_center - box.width / 2) * canvasSize.w
      const y1 = (box.y_center - box.height / 2) * canvasSize.h
      const w = box.width * canvasSize.w
      const h = box.height * canvasSize.h
      const isSelected = box.id === selectedBoxId

      // Box fill
      ctx.fillStyle = box.color + '20'
      ctx.fillRect(x1, y1, w, h)

      // Box border
      ctx.strokeStyle = box.color
      ctx.lineWidth = isSelected ? 2.5 : 1.5
      if (isSelected) ctx.setLineDash([])
      else ctx.setLineDash([])
      ctx.strokeRect(x1, y1, w, h)

      // Label — bottom-left inside the box
      const label = `${box.class_name}${box.confidence ? ` ${(box.confidence * 100).toFixed(0)}%` : ''}`
      ctx.font = '11px -apple-system, sans-serif'
      const metrics = ctx.measureText(label)
      const labelH = 16
      const labelW = metrics.width + 8
      const labelX = x1
      const labelY = y1 + h - labelH
      ctx.fillStyle = box.color
      ctx.fillRect(labelX, labelY, labelW, labelH)
      ctx.fillStyle = '#fff'
      ctx.fillText(label, labelX + 4, labelY + 12)

      // Resize handles (selected only)
      if (isSelected) {
        ctx.fillStyle = box.color
        const hs = HANDLE_SIZE
        ctx.fillRect(x1 - hs / 2, y1 - hs / 2, hs, hs)               // NW
        ctx.fillRect(x1 + w - hs / 2, y1 - hs / 2, hs, hs)           // NE
        ctx.fillRect(x1 - hs / 2, y1 + h - hs / 2, hs, hs)           // SW
        ctx.fillRect(x1 + w - hs / 2, y1 + h - hs / 2, hs, hs)       // SE
      }
    }

    // Drawing preview
    if (mode === 'drawing' && drawStart && drawCurrent) {
      const x = Math.min(drawStart.x, drawCurrent.x)
      const y = Math.min(drawStart.y, drawCurrent.y)
      const w = Math.abs(drawCurrent.x - drawStart.x)
      const h = Math.abs(drawCurrent.y - drawStart.y)

      ctx.fillStyle = activeColor + '20'
      ctx.fillRect(x, y, w, h)
      ctx.strokeStyle = activeColor
      ctx.lineWidth = 2
      ctx.setLineDash([4, 4])
      ctx.strokeRect(x, y, w, h)
      ctx.setLineDash([])
    }
  }, [boxes, canvasSize, selectedBoxId, mode, drawStart, drawCurrent, activeColor])

  useEffect(() => { draw() }, [draw])

  // Hit test — find which box (if any) was clicked
  const hitTest = useCallback((px: number, py: number): { box: BBox; handle: ResizeHandle | null } | null => {
    const norm = toNorm(px, py)

    // Check resize handles first (selected box only)
    if (selectedBoxId) {
      const sel = boxes.find(b => b.id === selectedBoxId)
      if (sel) {
        const x1 = (sel.x_center - sel.width / 2) * canvasSize.w
        const y1 = (sel.y_center - sel.height / 2) * canvasSize.h
        const x2 = x1 + sel.width * canvasSize.w
        const y2 = y1 + sel.height * canvasSize.h
        const hs = HANDLE_SIZE * 1.5

        if (Math.abs(px - x1) < hs && Math.abs(py - y1) < hs) return { box: sel, handle: 'nw' }
        if (Math.abs(px - x2) < hs && Math.abs(py - y1) < hs) return { box: sel, handle: 'ne' }
        if (Math.abs(px - x1) < hs && Math.abs(py - y2) < hs) return { box: sel, handle: 'sw' }
        if (Math.abs(px - x2) < hs && Math.abs(py - y2) < hs) return { box: sel, handle: 'se' }
      }
    }

    // Check box bodies (reverse order — top boxes first)
    for (let i = boxes.length - 1; i >= 0; i--) {
      const b = boxes[i]
      const bx1 = b.x_center - b.width / 2
      const by1 = b.y_center - b.height / 2
      const bx2 = bx1 + b.width
      const by2 = by1 + b.height
      if (norm.x >= bx1 && norm.x <= bx2 && norm.y >= by1 && norm.y <= by2) {
        return { box: b, handle: null }
      }
    }

    return null
  }, [boxes, selectedBoxId, canvasSize, toNorm])

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e)
    const hit = hitTest(pos.x, pos.y)

    if (hit?.handle) {
      // Start resize
      setMode('resizing')
      setResizeHandle(hit.handle)
      setResizeOriginal({ ...hit.box })
      setDrawStart(pos)
      onSelect(hit.box)
    } else if (hit?.box) {
      // Start move
      setMode('moving')
      const boxPx = toPixel(hit.box.x_center, hit.box.y_center)
      setDragOffset({ dx: pos.x - boxPx.x, dy: pos.y - boxPx.y })
      onSelect(hit.box)
    } else {
      // Start drawing new box
      setMode('drawing')
      setDrawStart(pos)
      setDrawCurrent(pos)
      onSelect(null)
    }
  }, [getMousePos, hitTest, onSelect, toPixel])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e)

    if (mode === 'drawing') {
      setDrawCurrent(pos)
    } else if (mode === 'moving' && selectedBoxId && dragOffset) {
      const norm = toNorm(pos.x - dragOffset.dx, pos.y - dragOffset.dy)
      const updated = boxes.map(b =>
        b.id === selectedBoxId
          ? { ...b, x_center: norm.x, y_center: norm.y, source: 'corrected' as const }
          : b
      )
      onChange(updated)
    } else if (mode === 'resizing' && resizeHandle && resizeOriginal && drawStart) {
      const orig = resizeOriginal
      const ox1 = orig.x_center - orig.width / 2
      const oy1 = orig.y_center - orig.height / 2
      const ox2 = ox1 + orig.width
      const oy2 = oy1 + orig.height

      const norm = toNorm(pos.x, pos.y)
      let nx1 = ox1, ny1 = oy1, nx2 = ox2, ny2 = oy2

      if (resizeHandle.includes('w')) nx1 = norm.x
      if (resizeHandle.includes('e')) nx2 = norm.x
      if (resizeHandle.includes('n')) ny1 = norm.y
      if (resizeHandle.includes('s')) ny2 = norm.y

      // Ensure min size
      if (Math.abs(nx2 - nx1) < MIN_BOX_SIZE) return
      if (Math.abs(ny2 - ny1) < MIN_BOX_SIZE) return

      const updated = boxes.map(b =>
        b.id === resizeOriginal.id
          ? {
            ...b,
            x_center: (nx1 + nx2) / 2,
            y_center: (ny1 + ny2) / 2,
            width: Math.abs(nx2 - nx1),
            height: Math.abs(ny2 - ny1),
            source: 'corrected' as const,
          }
          : b
      )
      onChange(updated)
    } else {
      // Cursor style
      const canvas = canvasRef.current
      if (!canvas) return
      const hit = hitTest(pos.x, pos.y)
      if (hit?.handle) canvas.style.cursor = hit.handle === 'nw' || hit.handle === 'se' ? 'nwse-resize' : 'nesw-resize'
      else if (hit?.box) canvas.style.cursor = 'move'
      else canvas.style.cursor = 'crosshair'
    }
  }, [mode, getMousePos, selectedBoxId, dragOffset, toNorm, boxes, onChange, resizeHandle, resizeOriginal, drawStart, hitTest])

  const handleMouseUp = useCallback(() => {
    if (mode === 'drawing' && drawStart && drawCurrent) {
      const norm1 = toNorm(drawStart.x, drawStart.y)
      const norm2 = toNorm(drawCurrent.x, drawCurrent.y)

      const w = Math.abs(norm2.x - norm1.x)
      const h = Math.abs(norm2.y - norm1.y)

      if (w > MIN_BOX_SIZE && h > MIN_BOX_SIZE) {
        const newBox: BBox = {
          id: generateId(),
          class_id: activeClassId,
          class_name: activeClassName,
          x_center: (norm1.x + norm2.x) / 2,
          y_center: (norm1.y + norm2.y) / 2,
          width: w,
          height: h,
          confidence: null,
          source: 'manual',
          color: activeColor,
        }
        onChange([...boxes, newBox])
        onSelect(newBox)
      }
    }

    setMode('idle')
    setDrawStart(null)
    setDrawCurrent(null)
    setDragOffset(null)
    setResizeHandle(null)
    setResizeOriginal(null)
  }, [mode, drawStart, drawCurrent, toNorm, activeClassId, activeClassName, activeColor, boxes, onChange, onSelect])

  // Keyboard: delete selected box
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedBoxId) {
        e.preventDefault()
        onChange(boxes.filter(b => b.id !== selectedBoxId))
        onSelect(null)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedBoxId, boxes, onChange, onSelect])

  return (
    <div ref={containerRef} className="w-full flex justify-center bg-black/5 dark:bg-white/5 rounded-lg overflow-hidden">
      {!imageLoaded ? (
        <div className="flex items-center justify-center h-96 text-muted-foreground text-sm">
          Loading image...
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          width={canvasSize.w}
          height={canvasSize.h}
          style={{ width: canvasSize.w, height: canvasSize.h }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      )}
    </div>
  )
}
